import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ProgressBar } from 'react-bootstrap';
import Select from 'react-select';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import styledComponent from '../../utils/styledComponent';
import track from '../../utils/analytics';
import { CreatePalletMutation } from '../../libs/GraphQL';
import {
  buildPalletInput,
  isAlreadyExistsError,
  normalizePalletQr,
} from './palletUtils';
import useFetchPallets from './useFetchPallets';
import PalletQrScanner, { FEEDBACK_DISPLAY_MS } from './PalletQrScanner';

const SectionHeader = styledComponent('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  '& h4': {
    margin: 0,
    fontWeight: 500,
  },
  '& p': {
    margin: '5px 0 0 0',
    color: '#818387',
    fontSize: '14px',
  },
});

const FilterGroup = styledComponent('div', {
  display: 'inline-block',
  minWidth: '200px',
  '& label': {
    color: '#000',
    fontSize: '18px',
    fontWeight: 800,
    marginBottom: '10px',
    display: 'block',
  },
  '& .SingleDatePickerInput__withBorder': {
    border: 'none',
  },
  '& .SingleDatePicker': {
    display: 'block',
  },
  '& .SingleDatePicker input': {
    background: '#FFF',
    border: '1px solid #CCC',
    borderRadius: '4px',
    fontSize: '16px',
    height: '50px',
    padding: '15px',
    width: '200px',
  },
});

const selectStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: '4px',
    border: '1px solid #CCC',
    minHeight: '50px',
    minWidth: '200px',
  }),
};

const SetupCard = styledComponent('div', {
  maxWidth: '500px',
  margin: '50px auto',
  padding: '40px',
  background: '#fff',
  border: '1px solid #ddd',
  borderRadius: '4px',
  '& h5': {
    textAlign: 'center',
    marginBottom: '30px',
    fontWeight: 500,
  },
  '& .form-group': {
    marginBottom: '20px',
  },
});

const ScanArea = styledComponent('div', {
  textAlign: 'center',
  padding: '40px',
  background: '#f6f8f5',
  border: '2px dashed #ccc',
  borderRadius: '8px',
  marginBottom: '20px',
});

const ScanInput = styledComponent('input', {
  fontSize: '18px',
  padding: '15px 20px',
  width: '100%',
  maxWidth: '400px',
  textAlign: 'center',
  height: 'auto',
  background: '#FFF',
  border: '1px solid #CCC',
  borderRadius: '4px',
});

const ScanInputHelpText = styledComponent('p', {
  color: '#818387',
  marginBottom: '15px',
});

const ProgressSection = styledComponent('div', {
  background: '#e8f4fd',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '20px',
});

const SummaryCard = styledComponent('div', {
  background: '#f6f8f5',
  border: '1px solid #ddd',
  borderRadius: '4px',
  padding: '20px',
  marginBottom: '20px',
});

const SummaryTitle = styledComponent('div', {
  fontWeight: 700,
  fontSize: '16px',
  marginBottom: '15px',
  color: '#2c2d31',
});

const Timer = styledComponent('div', {
  fontSize: '24px',
  fontWeight: 700,
  color: '#d9283f',
});

const WarningBanner = styledComponent('div', {
  background: '#fff3cd',
  border: '1px solid #ff9e22',
  color: '#856404',
  padding: '12px 16px',
  borderRadius: '4px',
  marginBottom: '20px',
  fontSize: '14px',
});

const ScanList = styledComponent('ul', {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  '& li': {
    padding: '10px 0',
    borderBottom: '1px solid #eee',
    fontSize: '16px',
  },
});

const StartButton = styledComponent('button', {
  backgroundColor: '#368007',
  border: 'none',
  borderRadius: '4px',
  color: '#fff',
  fontSize: '14px',
  padding: '15px',
  width: '100%',
  marginTop: '30px',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: '#008a00',
  },
  ':disabled': {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
});

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// Values must be valid backend PalletVarianceType enum values.
const VARIANCE_REASON_OPTIONS = [
  { value: 'MISSING_AT_RECEIVING', label: 'Missing at receiving' },
  { value: 'WRONG_LOCATION', label: 'Wrong location' },
];

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const ReceiveScanningTab = ({ selectedDate, onDateChange }) => {
  const [isDateFocused, setIsDateFocused] = useState(false);
  const { pallets, isLoading, error, fetchPallets } =
    useFetchPallets(selectedDate);
  const [userEmail, setUserEmail] = useState('');

  // Setup state
  const [linehaulFilter, setLinehaulFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  // Scanning state
  const [scannedPallets, setScannedPallets] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [scanError, setScanError] = useState(null);

  // Modal state
  const [showVarianceModal, setShowVarianceModal] = useState(false);
  const [showWrongPalletModal, setShowWrongPalletModal] = useState(false);
  const [wrongPalletInfo, setWrongPalletInfo] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [varianceReason, setVarianceReason] = useState('MISSING_AT_RECEIVING');
  const [varianceNotes, setVarianceNotes] = useState('');
  const [varianceError, setVarianceError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timerRef = useRef(null);
  const timeoutRef = useRef(null);
  const scanInputRef = useRef(null);

  // Fetch current user email
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const session = await Auth.currentAuthenticatedUser();
        setUserEmail(session.attributes.email);
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
    fetchUser();
  }, []);

  // Extract linehaul options
  const linehaulOptions = useMemo(() => {
    const linehauls = new Set();
    pallets.forEach((p) => {
      if (p.linehaulFirstMile) linehauls.add(p.linehaulFirstMile);
    });
    return Array.from(linehauls).map((v) => ({ value: v, label: v }));
  }, [pallets]);

  // Extract location options based on selected linehaul
  const locationOptions = useMemo(() => {
    const locations = new Set();
    pallets
      .filter(
        (p) => !linehaulFilter || p.linehaulFirstMile === linehaulFilter.value,
      )
      .forEach((p) => {
        if (p.market) locations.add(p.market);
      });
    return Array.from(locations).map((v) => ({ value: v, label: v }));
  }, [pallets, linehaulFilter]);

  // Pallets for selected linehaul + location
  const filteredPallets = useMemo(() => {
    if (!linehaulFilter || !locationFilter) return [];
    return pallets.filter(
      (p) =>
        p.linehaulFirstMile === linehaulFilter.value &&
        p.market === locationFilter.value,
    );
  }, [pallets, linehaulFilter, locationFilter]);

  // Remaining (unscanned) pallets
  const remainingPallets = useMemo(() => {
    const scannedIds = new Set(scannedPallets.map((s) => s.uniqueId));
    return filteredPallets.filter(
      (p) => !scannedIds.has(p.uniqueId) && p.loadStatus !== 'RECEIVED',
    );
  }, [filteredPallets, scannedPallets]);

  const progressPercent =
    filteredPallets.length > 0
      ? Math.round((scannedPallets.length / filteredPallets.length) * 100)
      : 0;

  // Timer
  useEffect(() => {
    if (isScanning) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);

      timeoutRef.current = setTimeout(() => {
        handleCompleteWithVariance();
      }, SESSION_TIMEOUT_MS);

      return () => {
        clearInterval(timerRef.current);
        clearTimeout(timeoutRef.current);
      };
    }
    return undefined;
  }, [isScanning]);

  useEffect(() => {
    if (
      isScanning &&
      scanInputRef.current &&
      !window.matchMedia('(pointer: coarse)').matches
    ) {
      scanInputRef.current.focus();
    }
  }, [isScanning]);

  // Reset location when linehaul changes
  useEffect(() => {
    setLocationFilter(null);
  }, [linehaulFilter]);

  const startScanning = () => {
    // Pre-populate with already received pallets
    const alreadyReceived = filteredPallets
      .filter((p) => p.loadStatus === 'RECEIVED')
      .map((p) => ({
        ...p,
        scannedAt: 'previously',
      }));
    setScannedPallets(alreadyReceived);
    setElapsedSeconds(0);
    setScanError(null);
    setIsScanning(true);
  };

  const cancelScanning = () => {
    track('Receive Scanning Session Cancelled', {
      linehaul: linehaulFilter?.value,
      location: locationFilter?.value || null,
      palletsScanned: scannedPallets.length,
      palletsExpected: filteredPallets.length,
      elapsedSeconds,
    });
    clearInterval(timerRef.current);
    clearTimeout(timeoutRef.current);
    setIsScanning(false);
    setScannedPallets([]);
    setElapsedSeconds(0);
    setScanError(null);
  };

  const handleScan = useCallback(
    async (qrCode) => {
      setScanError(null);

      // Find the pallet matching this QR code. Labels are printed with the
      // PENDING status baked in, so compare status-agnostically.
      const normalizedCode = normalizePalletQr(qrCode);
      const matchedPallet = pallets.find(
        (p) =>
          normalizePalletQr(p.qrCode) === normalizedCode ||
          normalizePalletQr(p.uniqueId) === normalizedCode,
      );

      if (!matchedPallet) {
        setScanError('Pallet not found. Please enter a valid pallet QR code.');
        return { status: 'error', message: 'Pallet not found' };
      }

      // Check if already scanned
      if (scannedPallets.find((s) => s.uniqueId === matchedPallet.uniqueId)) {
        toast.info('This pallet has already been scanned.', {
          toastId: matchedPallet.uniqueId,
          autoClose: FEEDBACK_DISPLAY_MS,
        });
        return { status: 'info', message: 'Pallet already scanned' };
      }

      // Check if pallet belongs to the selected linehaul and location
      if (
        matchedPallet.linehaulFirstMile !== linehaulFilter.value ||
        matchedPallet.market !== locationFilter.value
      ) {
        setWrongPalletInfo({
          pallet: matchedPallet,
          expectedLinehaul: linehaulFilter.value,
          expectedLocation: locationFilter.value,
        });
        setShowWrongPalletModal(true);
        return {
          status: 'error',
          message: 'Wrong linehaul/location for this pallet',
        };
      }

      // Create pallet record with RECEIVED status
      try {
        if (matchedPallet.loadStatus !== 'RECEIVED') {
          await CreatePalletMutation(
            buildPalletInput(matchedPallet, {
              loadStatus: 'RECEIVED',
              receivedAtLocation: locationFilter.value,
              createdBy: userEmail,
            }),
          );
        }
      } catch (err) {
        if (!isAlreadyExistsError(err)) {
          console.error('Error updating pallet status:', err);
          setScanError('Failed to update pallet status. Please try again.');
          return { status: 'error', message: 'Failed to update pallet status' };
        }
      }

      {
        const scannedEntry = {
          ...matchedPallet,
          scannedAt: new Date().toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
        };

        const newScanned = [...scannedPallets, scannedEntry];
        setScannedPallets(newScanned);
        toast.success(
          `Pallet scanned: ${newScanned.length} of ${filteredPallets.length}`,
          { toastId: matchedPallet.uniqueId, autoClose: FEEDBACK_DISPLAY_MS },
        );

        // Check if all pallets scanned
        if (newScanned.length === filteredPallets.length) {
          clearInterval(timerRef.current);
          clearTimeout(timeoutRef.current);
          setShowSuccessModal(true);
        }

        return {
          status: 'success',
          message: `Pallet scanned: ${newScanned.length} of ${filteredPallets.length}`,
        };
      }
    },
    [
      pallets,
      scannedPallets,
      linehaulFilter,
      locationFilter,
      filteredPallets,
      userEmail,
    ],
  );

  const handleScanSubmit = (e) => {
    e.preventDefault();
    if (scanInput.trim()) {
      handleScan(scanInput.trim());
      setScanInput('');
    }
  };

  const handleCompleteWithVariance = () => {
    setVarianceError(null);
    setShowVarianceModal(true);
  };

  const selectedVarianceReason =
    VARIANCE_REASON_OPTIONS.find((o) => o.value === varianceReason) ||
    VARIANCE_REASON_OPTIONS[0];

  const handleConfirmVariance = async () => {
    setIsSubmitting(true);
    setVarianceError(null);
    try {
      const scannedIds = new Set(scannedPallets.map((s) => s.uniqueId));
      const missingPallets = filteredPallets.filter(
        (p) => !scannedIds.has(p.uniqueId),
      );

      await Promise.all(
        missingPallets.map((p) =>
          CreatePalletMutation(
            buildPalletInput(p, {
              loadStatus: 'VARIANCE',
              varianceType: varianceReason,
              varianceNotes,
              createdBy: userEmail,
            }),
          ).catch((err) => {
            if (!isAlreadyExistsError(err)) throw err;
          }),
        ),
      );

      clearInterval(timerRef.current);
      clearTimeout(timeoutRef.current);
      setShowVarianceModal(false);
      setIsScanning(false);
      setScannedPallets([]);
      setElapsedSeconds(0);
      setVarianceReason('MISSING_AT_RECEIVING');
      setVarianceNotes('');
      fetchPallets();
    } catch (err) {
      console.error('Error recording variance:', err);
      setVarianceError(
        err?.errors?.[0]?.message ||
          err?.message ||
          'Failed to record variance. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSwitchLinehaul = () => {
    if (wrongPalletInfo) {
      setLinehaulFilter({
        value: wrongPalletInfo.pallet.linehaulFirstMile,
        label: wrongPalletInfo.pallet.linehaulFirstMile,
      });
      setLocationFilter({
        value: wrongPalletInfo.pallet.market,
        label: wrongPalletInfo.pallet.market,
      });
      setShowWrongPalletModal(false);
      setWrongPalletInfo(null);
      cancelScanning();
    }
  };

  const handleSuccessDone = () => {
    setShowSuccessModal(false);
    setIsScanning(false);
    setScannedPallets([]);
    setElapsedSeconds(0);
    fetchPallets();
  };

  const missingPallets = useMemo(() => {
    const scannedIds = new Set(scannedPallets.map((s) => s.uniqueId));
    return filteredPallets.filter((p) => !scannedIds.has(p.uniqueId));
  }, [filteredPallets, scannedPallets]);

  // Setup view
  if (!isScanning) {
    return (
      <div style={{ paddingTop: '20px' }}>
        <SectionHeader>
          <div>
            <h4>Receive Scanning</h4>
            <p>Scan pallets at receiving destination</p>
          </div>
        </SectionHeader>

        <SetupCard>
          <h5>
            Select production date, linehaul, and location to begin scanning
          </h5>

          <div className="form-group">
            <FilterGroup>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="receive-production-date">Production Date</label>
              <SingleDatePicker
                date={selectedDate}
                isOutsideRange={() => false}
                onDateChange={onDateChange}
                focused={isDateFocused}
                onFocusChange={({ focused }) => setIsDateFocused(focused)}
                id="receive-production-date"
                numberOfMonths={1}
              />
            </FilterGroup>
          </div>

          <div className="form-group">
            <FilterGroup>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="receive-linehaul">Linehaul</label>
              <Select
                inputId="receive-linehaul"
                options={linehaulOptions}
                value={linehaulFilter}
                onChange={setLinehaulFilter}
                isClearable
                placeholder="Select Linehaul..."
                styles={selectStyles}
                isLoading={isLoading}
              />
            </FilterGroup>
          </div>

          <div className="form-group">
            <FilterGroup>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="receive-location">Receiving Location</label>
              <Select
                inputId="receive-location"
                options={locationOptions}
                value={locationFilter}
                onChange={setLocationFilter}
                isClearable
                placeholder="Select Location..."
                styles={selectStyles}
                isDisabled={!linehaulFilter}
              />
            </FilterGroup>
          </div>

          {error && (
            <WarningBanner>
              <strong>Error:</strong> {error}
            </WarningBanner>
          )}

          <StartButton
            onClick={startScanning}
            disabled={
              !selectedDate ||
              !linehaulFilter ||
              !locationFilter ||
              filteredPallets.length === 0
            }
          >
            Start Scanning
          </StartButton>

          {linehaulFilter &&
            locationFilter &&
            !isLoading &&
            filteredPallets.length === 0 && (
              <p
                style={{
                  textAlign: 'center',
                  color: '#818387',
                  marginTop: '15px',
                }}
              >
                No pallets found for this linehaul, location, and date.
              </p>
            )}
        </SetupCard>
      </div>
    );
  }

  // Active scanning view
  return (
    <div style={{ paddingTop: '20px' }}>
      <SectionHeader>
        <div>
          <h4>
            Receive Scanning - {linehaulFilter?.value} &rarr;{' '}
            {locationFilter?.value}
          </h4>
          <p>Scan pallets at receiving destination</p>
        </div>
        <Timer>
          <span className="glyphicon glyphicon-time" />{' '}
          {formatTime(elapsedSeconds)} elapsed
        </Timer>
      </SectionHeader>

      <WarningBanner>
        <strong>Session Timeout:</strong> Complete scanning within 30 minutes or
        current scan count will be recorded as final (with variance if
        incomplete).
      </WarningBanner>

      <SummaryCard>
        <SummaryTitle>RECEIVING SUMMARY - {locationFilter?.value}</SummaryTitle>
        <div className="row">
          <div className="col-sm-4">
            <strong>Pallets Expected:</strong> {filteredPallets.length}
            <br />
            <strong>Linehaul:</strong> {linehaulFilter?.value}
          </div>
          <div className="col-sm-8">
            <span>
              All {filteredPallets.length} pallets destined for{' '}
              {locationFilter?.value}
            </span>
          </div>
        </div>
      </SummaryCard>

      <form onSubmit={handleScanSubmit}>
        <ScanArea>
          <h5>Scan Pallet QR Code</h5>
          <PalletQrScanner
            onScan={handleScan}
            disabled={
              showWrongPalletModal || showVarianceModal || showSuccessModal
            }
          />
          <ScanInputHelpText>
            Or type the pallet QR code below and press Enter.
          </ScanInputHelpText>
          <ScanInput
            ref={scanInputRef}
            type="text"
            placeholder="Enter QR code..."
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
          />
        </ScanArea>
      </form>

      {scanError && (
        <WarningBanner>
          <strong>Error:</strong> {scanError}
        </WarningBanner>
      )}

      <ProgressSection>
        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
          <strong style={{ fontSize: '18px' }}>
            {scannedPallets.length} of {filteredPallets.length} pallets scanned
          </strong>
        </div>
        <ProgressBar now={progressPercent} label={`${progressPercent}%`} />
      </ProgressSection>

      <div className="row">
        <div className="col-sm-6">
          <h5 style={{ color: '#368007' }}>
            <span className="glyphicon glyphicon-ok" /> SCANNED
          </h5>
          <ScanList>
            {scannedPallets.map((p) => (
              <li key={p.uniqueId} style={{ color: '#368007' }}>
                <span className="glyphicon glyphicon-ok" /> Pallet{' '}
                {p.palletSequence}/{filteredPallets.length} - {p.market}{' '}
                <small style={{ color: '#818387' }}>({p.scannedAt})</small>
              </li>
            ))}
          </ScanList>
        </div>
        <div className="col-sm-6">
          <h5 style={{ color: '#818387' }}>
            <span className="glyphicon glyphicon-unchecked" /> REMAINING
          </h5>
          <ScanList>
            {remainingPallets.map((p) => (
              <li key={p.uniqueId} style={{ color: '#818387' }}>
                <span className="glyphicon glyphicon-unchecked" /> Pallet{' '}
                {p.palletSequence}/{filteredPallets.length} - {p.market}
              </li>
            ))}
          </ScanList>
        </div>
      </div>

      <hr />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={cancelScanning}>Cancel Session</Button>
        <Button bsStyle="warning" onClick={handleCompleteWithVariance}>
          Complete with Variance
        </Button>
      </div>

      {/* Variance Modal */}
      <Modal
        show={showVarianceModal}
        onHide={() => setShowVarianceModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: '#bd4b00' }}>
            <span className="glyphicon glyphicon-warning-sign" /> Complete with
            Variance
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {varianceError && (
            <WarningBanner>
              <strong>Error:</strong> {varianceError}
            </WarningBanner>
          )}
          <p>
            You are completing with{' '}
            <strong>
              {scannedPallets.length} of {filteredPallets.length}
            </strong>{' '}
            pallets scanned.
          </p>
          <p>
            <strong>{missingPallets.length} pallets</strong> will be marked as{' '}
            {selectedVarianceReason.label}.
          </p>

          <div style={{ marginBottom: '20px' }}>
            <strong>Missing Pallets:</strong>
            <ul style={{ marginTop: '10px' }}>
              {missingPallets.map((p) => (
                <li key={p.uniqueId}>
                  Pallet {p.palletSequence}/{filteredPallets.length} -{' '}
                  {p.market}
                </li>
              ))}
            </ul>
          </div>

          <div className="form-group">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="receive-variance-reason"
              style={{ fontWeight: 700 }}
            >
              Variance Reason
            </label>
            <select
              id="receive-variance-reason"
              className="form-control"
              value={varianceReason}
              onChange={(e) => setVarianceReason(e.target.value)}
            >
              {VARIANCE_REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="receive-variance-notes" style={{ fontWeight: 700 }}>
              Notes (optional)
            </label>
            <textarea
              id="receive-variance-notes"
              className="form-control"
              rows="3"
              style={{ height: 'auto' }}
              placeholder="Add additional details about this variance..."
              value={varianceNotes}
              onChange={(e) => setVarianceNotes(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowVarianceModal(false)}>Cancel</Button>
          <Button
            bsStyle="danger"
            onClick={handleConfirmVariance}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Recording...' : 'Confirm & Record Variance'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Wrong Pallet Modal */}
      <Modal
        show={showWrongPalletModal}
        onHide={() => setShowWrongPalletModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: '#d9283f' }}>
            <span className="glyphicon glyphicon-warning-sign" /> Wrong Pallet
            Scanned
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This pallet belongs to a{' '}
            <strong>different linehaul/location</strong>.
          </p>
          {wrongPalletInfo && (
            <table className="table" style={{ fontSize: '14px' }}>
              <tbody>
                <tr>
                  <td>
                    <strong>Scanned Pallet:</strong>
                  </td>
                  <td>
                    Pallet {wrongPalletInfo.pallet.palletSequence} -{' '}
                    {wrongPalletInfo.pallet.market}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Expected Linehaul:</strong>
                  </td>
                  <td>{wrongPalletInfo.expectedLinehaul}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Expected Location:</strong>
                  </td>
                  <td>{wrongPalletInfo.expectedLocation}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Pallet Linehaul:</strong>
                  </td>
                  <td>{wrongPalletInfo.pallet.linehaulFirstMile}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Pallet Location:</strong>
                  </td>
                  <td>{wrongPalletInfo.pallet.market}</td>
                </tr>
              </tbody>
            </table>
          )}
          <p style={{ color: '#818387' }}>
            Please scan a pallet for {locationFilter?.value}, or change your
            selected linehaul/location.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowWrongPalletModal(false);
              setWrongPalletInfo(null);
            }}
          >
            Dismiss &amp; Retry
          </Button>
          <Button bsStyle="info" onClick={handleSwitchLinehaul}>
            Switch to {wrongPalletInfo?.pallet.linehaulFirstMile} /{' '}
            {wrongPalletInfo?.pallet.market}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={handleSuccessDone}>
        <Modal.Body style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', color: '#368007' }}>
            <span className="glyphicon glyphicon-ok-circle" />
          </div>
          <h4 style={{ color: '#368007', marginTop: '20px' }}>
            Receiving Complete!
          </h4>
          <p>
            All <strong>{filteredPallets.length} pallets</strong> successfully
            scanned and marked as RECEIVED.
          </p>
          <table
            className="table"
            style={{ fontSize: '14px', marginTop: '20px' }}
          >
            <tbody>
              <tr>
                <td>
                  <strong>Linehaul:</strong>
                </td>
                <td>{linehaulFilter?.value}</td>
              </tr>
              <tr>
                <td>
                  <strong>Location:</strong>
                </td>
                <td>{locationFilter?.value}</td>
              </tr>
              <tr>
                <td>
                  <strong>Total Pallets:</strong>
                </td>
                <td>{filteredPallets.length}</td>
              </tr>
              <tr>
                <td>
                  <strong>Time Taken:</strong>
                </td>
                <td>{formatTime(elapsedSeconds)}</td>
              </tr>
            </tbody>
          </table>
          <Button
            style={{
              marginTop: '20px',
              padding: '12px 40px',
              backgroundColor: '#368007',
              color: '#fff',
              border: 'none',
            }}
            onClick={handleSuccessDone}
          >
            Done
          </Button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

ReceiveScanningTab.propTypes = {
  selectedDate: PropTypes.object,
  onDateChange: PropTypes.func.isRequired,
};

ReceiveScanningTab.defaultProps = {
  selectedDate: null,
};

export default ReceiveScanningTab;
